/* eslint-disable camelcase */
import { useQuery } from '@tanstack/react-query'
import { ReactQueryKeys, SearchType, Topic } from '@/types'
import axios from 'axios'
import {
  DEFAULT_TOPIC_APPLICATION,
  PrivacyType,
  TopicTypeAndApplicationDic,
} from '@/utils/constants'

export interface GetPublicTopicParam {
  pageNo: number
  interaction: number
  topicType: number
}

export const useGetPublicTopics = (params: GetPublicTopicParam) => {
  return useQuery({
    queryKey: [ReactQueryKeys.GET_TOPICS, params.topicType, params.pageNo],
    queryFn: async () => {
      try {
        // Create a standalone axios instance without the auth interceptor
        const publicApi = axios.create({
          baseURL: process.env.REACT_APP_API_URL,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          withCredentials: true, // API expects cookies
          timeout: 60000,
        })

        const requestData = {
          requestType: 'topicList',
          list: 'mini',
          format: 'json',
          searchType: SearchType.OtherUser,
          topicApplication:
            TopicTypeAndApplicationDic[params.topicType] || DEFAULT_TOPIC_APPLICATION,
          pageNo: params.pageNo,
          interaction: PrivacyType.PUBLIC,
          topicType: params.topicType,
          connID: process.env.REACT_APP_GUEST_USER_ID,
        }

        console.log('🔍 [PUBLIC TOPICS] Request Data:', requestData)
        console.log('📍 [PUBLIC TOPICS] API URL:', publicApi.defaults.baseURL)

        const response = await publicApi.post('/ajax/topics', requestData)

        console.log('📦 [PUBLIC TOPICS] Response:', {
          status: response.status,
          myTopicsCount: response.data?.myTopics?.length || 0,
          isError: response.data?.isError,
          errorMessage: response.data?.errorMessage,
        })

        // If API returns an error, throw it
        if (response.data?.isError) {
          console.error('❌ [PUBLIC TOPICS] API Error:', response.data.errorMessage)
          throw new Error(response.data?.errorMessage || 'API returned an error')
        }

        const topics = response.data?.myTopics || []
        console.log(`✅ [PUBLIC TOPICS] Successfully fetched ${topics.length} topics`)
        return topics as Topic[]
      } catch (error: any) {
        console.error('❌ [PUBLIC TOPICS] Request Failed:', {
          message: error.message,
          responseData: error.response?.data,
          responseStatus: error.response?.status,
          responseHeaders: error.response?.headers,
          requestURL: error.config?.url,
          fullError: error,
        })
        throw error
      }
    },
    enabled: params.topicType !== undefined && params.topicType !== null,
    retry: 2,
    retryDelay: 1000,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  })
}
